import type { DiscussionNode } from '@proref/types';
import type { CreateDiscussionInput, UpdateDiscussionInput } from '@proref/schemas';
import { api, unwrap, type Envelope } from '../lib/api';

// The vote endpoints return the resulting count + whether the requester now holds a vote.
export interface VoteResult {
  upvotes: number;
  has_voted: boolean;
}

// A clip's thread (2-level: top-level comments each with a replies array). Gated server-side
// on having answered the clip — the caller only requests it once viewer.has_answered is true.
export async function listDiscussions(clipId: string): Promise<DiscussionNode[]> {
  const res = await api.get<Envelope<DiscussionNode[]>>(`/clips/${clipId}/discussions`);
  return unwrap(res.data);
}

// Post a new comment or a reply (parent_id). Returns the created node.
export async function postComment(clipId: string, input: CreateDiscussionInput): Promise<DiscussionNode> {
  const res = await api.post<Envelope<DiscussionNode>>(`/clips/${clipId}/discussions`, input);
  return unwrap(res.data);
}

// Edit own comment text (author only, enforced server-side).
export async function editComment(id: string, input: UpdateDiscussionInput): Promise<DiscussionNode> {
  const res = await api.patch<Envelope<DiscussionNode>>(`/discussions/${id}`, input);
  return unwrap(res.data);
}

// Soft-delete a comment (author or moderator). The row stays as a tombstone server-side.
export async function deleteComment(id: string): Promise<void> {
  await api.delete<Envelope<null>>(`/discussions/${id}`);
}

// Toggle the requester's upvote. Idempotent server-side, so a double-tap is harmless.
export async function voteComment(id: string): Promise<VoteResult> {
  const res = await api.post<Envelope<VoteResult>>(`/discussions/${id}/vote`);
  return unwrap(res.data);
}

export async function unvoteComment(id: string): Promise<VoteResult> {
  const res = await api.delete<Envelope<VoteResult>>(`/discussions/${id}/vote`);
  return unwrap(res.data);
}
